import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettings';
import { mediaUrl } from '@/admin/api';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSiteSettings();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Membership', path: '/membership' },
    { name: 'Events', path: '/events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'News', path: '/news' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-gradient-to-r from-black via-neutral-600 to-white backdrop-blur-md border-b border-white/20 shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            {settings.logo_url ? (
              <img src={mediaUrl(settings.logo_url)} alt={settings.site_name} className="w-12 h-12 rounded-full object-cover shadow-medium ring-1 ring-white/30" data-testid="navbar-logo" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-medium transition-transform duration-300 group-hover:scale-105">
                <span className="text-black font-bold text-xl font-serif">MT</span>
              </div>
            )}
            <div className="hidden md:block">
              <span className="text-xl font-serif font-bold text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]" data-testid="navbar-site-name">{settings.site_name}</span>
              <p className="text-xs text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.9)]">{settings.tagline}</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.9)] ${
                  isActive(link.path)
                    ? 'text-white bg-black/40 backdrop-blur-sm ring-1 ring-white/40'
                    : 'text-white hover:bg-white/15'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/payment">
              <Button className="bg-black hover:bg-black/85 text-white border border-black shadow-soft transition-elegant">
                Pay for 2026 Summit
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.9)] hover:bg-white/15 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden bg-gradient-to-r from-black via-neutral-700 to-neutral-300 backdrop-blur-md border-t border-white/20 shadow-medium">
          <div className="px-4 py-6 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all [text-shadow:0_1px_3px_rgba(0,0,0,0.9)] ${
                  isActive(link.path)
                    ? 'text-white bg-black/40 ring-1 ring-white/40'
                    : 'text-white hover:bg-white/15'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/payment" onClick={() => setIsOpen(false)}>
              <Button className="w-full bg-black hover:bg-black/85 text-white border border-black mt-4">
                Pay for 2026 Summit
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
